import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, Clock, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTEREST_LABELS = {
  general: "Demo general", erp: "Sistema ERP", crm: "CRM",
  inventory: "Inventarios", billing: "Facturacion", custom: "A medida",
};

const LeadNotifications = ({ auth }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const lastCheckRef = useRef(null);
  const audioRef = useRef(null);

  const playSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio("data:audio/wav;base64,UklGRlQGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YTAGAABnAJQA0AAQAV8BmAHHAeAB4wHUAbUBhgFIAf4AqABIAN//bv/4/oH+DP6a/S79yPxq/Bj82PuZ+2n7Svs6+zr7R/tN+1n7hvvC+w/8avzX/Fb97P2R/kj/CwDZAK4BiAJjAzgEBgXIBXgGFQedBwwIZAijCMcI1AjGCJ0IWQP6BX0F7AQ+BHcDlgKdAYoAYP8k/tb8gfsl+sH4W/fy9Yj0G/Ov8UTw3O537Xfsmuu66SnkxOSa5ZnmvecP6YnqJ+zo7cjvxfHb8wb2Rvib+gP9ev4LAi0EdQa6CPMKIg0/D0oRQhMfFd8WhRgFGmEbnxyyHaAeZB8AIHQQ");
      }
      audioRef.current.play().catch(() => {});
    } catch {}
  }, []);

  const checkNewLeads = useCallback(async () => {
    try {
      const params = lastCheckRef.current ? `?since=${encodeURIComponent(lastCheckRef.current)}` : "";
      const res = await axios.get(`${API}/admin/leads/recent${params}`, {
        auth: { username: auth.username, password: auth.password },
      });

      if (lastCheckRef.current && res.data.recent?.length > 0) {
        const newLeads = res.data.recent;
        setNotifications((prev) => [...newLeads.map((l) => ({ ...l, read: false })), ...prev].slice(0, 20));
        setUnreadCount((prev) => prev + newLeads.length);
        playSound();
        newLeads.forEach((lead) => {
          toast.success(`Nuevo lead: ${lead.name}`, {
            description: INTEREST_LABELS[lead.interest] || lead.source || "Demo",
          });
        });
      } else if (!lastCheckRef.current) {
        setNotifications(res.data.recent?.map((l) => ({ ...l, read: true })) || []);
      }

      lastCheckRef.current = new Date().toISOString();
    } catch {
    }
  }, [auth, playSound]);

  useEffect(() => {
    checkNewLeads();
    const interval = setInterval(checkNewLeads, 30000);
    return () => clearInterval(interval);
  }, [checkNewLeads]);

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }
  };

  return (
    <div className="relative">
      <motion.button
        onClick={handleOpen}
        className="relative w-10 h-10 flex items-center justify-center text-[#94a3b8] hover:text-white transition-colors rounded hover:bg-white/5"
        whileTap={{ scale: 0.9 }}
        data-testid="lead-notifications-btn"
      >
        <Bell className="w-5 h-5" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center px-1"
              data-testid="lead-notification-badge"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 top-12 w-80 bg-[#0b1221] border border-white/10 z-50 shadow-2xl rounded-sm overflow-hidden"
              data-testid="lead-notifications-panel"
            >
              <div className="p-3 border-b border-white/5 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold text-sm">Leads Recientes</h3>
                  <p className="text-[#94a3b8] text-[10px]">Notificaciones en tiempo real</p>
                </div>
                <button onClick={() => setIsOpen(false)} className="text-[#94a3b8] hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {notifications.length > 0 ? (
                  notifications.map((n, i) => (
                    <div
                      key={`${n.email}-${i}`}
                      className={`p-3 border-b border-white/5 hover:bg-white/5 transition-colors ${
                        !n.read ? "bg-[#3b82f6]/5 border-l-2 border-l-[#3b82f6]" : ""
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="w-7 h-7 bg-[#3b82f6]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <User className="w-3.5 h-3.5 text-[#3b82f6]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-medium truncate">{n.name}</p>
                          <p className="text-[#94a3b8] text-xs truncate">{n.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {n.interest && (
                              <span className="text-[10px] text-[#06b6d4] bg-[#06b6d4]/10 px-1.5 py-0.5">
                                {INTEREST_LABELS[n.interest] || n.interest}
                              </span>
                            )}
                            {n.created_at && (
                              <span className="text-[#94a3b8] text-[10px] flex items-center gap-0.5">
                                <Clock className="w-2.5 h-2.5" />
                                {new Date(n.created_at).toLocaleDateString("es", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-6 text-center">
                    <Bell className="w-8 h-8 text-[#3b82f6]/20 mx-auto mb-2" />
                    <p className="text-[#94a3b8] text-sm">Sin notificaciones</p>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LeadNotifications;
